import * as React from 'react';
import { Formik, FormikProps } from 'formik';
import { Row, Col, Button, Modal, Typography, Space, Table } from 'antd';
import * as Icons from '@ant-design/icons';
import { UploadField } from '@core/form';
import { AntForm, Details } from '@core/ui';

import { ImportDeclarationDto } from '../dto';
import { useDeclarationsExportTasks, useImportDeclaration } from '../hooks';
import { ImportDeclarationContext } from '../context';
import { IExportDeclarationTask } from '../interfaces';

const iconMap: Record<number, React.ReactNode> = {
  131: <Icons.FileDoneOutlined />,
  132: <Icons.LoadingOutlined />,
  134: <Icons.DownloadOutlined />,
};

const FormikComponent: React.FC<FormikProps<ImportDeclarationDto>> = ({ values, handleSubmit, isSubmitting }) => {
  const { onClose } = React.useContext(ImportDeclarationContext);

  const { data: exportTaskList, handleDownload, refetch, isCheckBtnEnabled } = useDeclarationsExportTasks();

  React.useEffect(() => {
    if (values.file) {
      handleSubmit();
    }
  }, [values.file, handleSubmit]);

  return (
    <Modal
      visible={true}
      width={768}
      onCancel={onClose}
      okButtonProps={{ hidden: true }}
      confirmLoading={isSubmitting}
      title={
        <div style={{ lineHeight: 1.2 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            İzləmə Kodları üzrə Məlumat İdxalı
          </Typography.Title>
          <Typography.Text type='secondary' style={{ fontSize: 12 }}>
            Excel faylında bir sütunda toplu izləmə kodlarınızı qeyd edin, sistem həmin bağlamaların daha geniş məlumatların təqdim edəcək.
          </Typography.Text>
        </div>
      }
    >
      <Details.Row>
        <Details.Col span={14}>
          <Details.Row>
            <Details.Col span={24}>
              <AntForm layout='vertical' component='div' size='large'>
                <Row gutter={[24, 0]}>
                  <Col xs={24}>
                    <UploadField draggable name='file'>
                      {() => (
                        <Space direction='vertical' size={[12, 12]}>
                          <Typography.Text type='secondary' style={{ fontSize: 14 }}>
                            Sənədi yükləmək üçün klikləyin və ya sürüşdürün
                          </Typography.Text>
                          <Button style={{ borderRadius: 8 }} icon={<Icons.UploadOutlined />}>
                            Import
                          </Button>
                        </Space>
                      )}
                    </UploadField>
                  </Col>
                </Row>
              </AntForm>
            </Details.Col>

            <Details.Col span={24}>
              <Table
                size='small'
                footer={() => (
                  <Button icon={<Icons.SearchOutlined />} disabled={!isCheckBtnEnabled} type='text' onClick={() => refetch()}>
                    Yoxla
                  </Button>
                )}
                pagination={{ pageSize: 5 }}
                bordered={true}
                dataSource={exportTaskList}
                rowKey='id'
              >
                <Table.Column
                  key='id'
                  dataIndex='id'
                  title='#'
                  render={(value, record: IExportDeclarationTask) => {
                    const isCompleted = record.status?.id === 134;
                    const btnValue = iconMap[record.status?.id] ?? value;
                    return (
                      <Button disabled={!isCompleted} onClick={() => handleDownload(value)} type='text'>
                        {btnValue}
                      </Button>
                    );
                  }}
                />
                <Table.Column key='status' dataIndex='status' title='Status' render={(value) => value.name} />
                <Table.Column key='createdAt' dataIndex='createdAt' title='Tarix' />
              </Table>
            </Details.Col>
          </Details.Row>
        </Details.Col>
        <Details.Col span={10}>
          <>
            <Typography.Title>Qaydalar</Typography.Title>
            <ul style={{ paddingLeft: 20 }}>
              <li>
                İmport ediləcək excel faylında <Typography.Text strong>izləmə kodları</Typography.Text> və ya <Typography.Text strong>qlobal izləmə kodları</Typography.Text> bir sütun boyunca qeyd
                edilməlidir.
              </li>
              <li>İzləmə və ya global izləmə kodlarını sistem özü ayıracaq.</li>
              <li>Qarşısında FDX prefixi olarsa, sistem özü silə bilər.</li>
              <li>İmport ediləcək excel faylının yalnız 1 sütunu olmalıdır.</li>
              <li>İmport uğurla qəbul olduqdan sonra, məlumatlar hazır olduqdan sonra soldakı siyahıda görünəcək.</li>
              <li>
                Nümunə üçün excel:&nbsp;
                <Typography.Link download href='/declarations.xlsx' rel='noreferrer noopener'>
                  Yüklə <Icons.DownloadOutlined />
                </Typography.Link>
              </li>
            </ul>
          </>
        </Details.Col>
      </Details.Row>
    </Modal>
  );
};

export const ImportDeclaration = () => {
  const { initialValues, onSubmit } = useImportDeclaration();

  if (!initialValues) {
    return null;
  }

  return <Formik initialValues={initialValues} enableReinitialize={true} onSubmit={onSubmit} component={FormikComponent} />;
};
